import { IProducts } from "../../type";
import {
  getHeadersWithUniqueVisitorId,
  ShoppingCartItem,
  UserRetailerShoppingCart,
  CartItem,
} from "@brandclub/common-ui";
import { HOST_DOMAIN } from "../../utils/domain";

const DOMAIN = HOST_DOMAIN + "/api-retailer-sync";

export interface AllUserRetailerShoppingCartsResponse {
  userRetailerShoppingCarts: UserRetailerShoppingCart[];
  products: IProducts;
  status?: string;
  cartItems: CartItem[];
}

export interface UpdateItemsToUserRetailerShoppingCartResponse {
  userRetailerShoppingCarts: UserRetailerShoppingCart[];
  status?: string;
}

export const getActiveCarts = async (
  signal?: AbortSignal
): Promise<AllUserRetailerShoppingCartsResponse> => {
  const headers = await getHeadersWithUniqueVisitorId();
  const res = await fetch(
    DOMAIN + `/retailerCheckout/getAllUserRetailerShoppingCarts`,
    {
      method: "GET",
      headers,
      signal,
    }
  );
  if (res.ok) {
    return await res.json();
  } else {
    throw new Error("Error fetching active carts");
  }
};

export const updateItemsToUserRetailerShoppingCart = async (
  updateItemsToUserRetailerShoppingCartRequest: {
    retailerId: number;
    items: ShoppingCartItem[];
  },
  signal?: AbortSignal
): Promise<UpdateItemsToUserRetailerShoppingCartResponse> => {
  const headers = await getHeadersWithUniqueVisitorId();
  const res = await fetch(
    DOMAIN + `/retailerCheckoutV2/updateItemsToUserRetailerShoppingCart`,
    {
      method: "POST",
      headers,
      signal,

      body: JSON.stringify(updateItemsToUserRetailerShoppingCartRequest),
    }
  );
  if (res.ok) {
    return await res.json();
  } else {
    throw new Error("Error updating items to user retailer shopping cart");
  }
};

const mapShoppingCartItemsToRequest = (
  items: ShoppingCartItem[]
): {
  brandId: number;
  stacklineSku: string;
  quantity: number;
  retailPrice: number;
  retailerSku: string;
  retailerId: number;
}[] => {
  return items.map((item) => ({
    brandId: item.brandId,
    stacklineSku: item.stacklineSku,
    quantity: item.quantity,
    retailPrice: item.retailPrice,
    retailerSku: item.retailerSku,
    retailerId: item.retailerId,
  }));
};

export const removeItemFromShoppingCart = async (
  {
    retailerId,
    items,
  }: {
    retailerId: number;
    items: ShoppingCartItem[];
  },
  signal?: AbortSignal
): Promise<UpdateItemsToUserRetailerShoppingCartResponse> => {
  const headers = await getHeadersWithUniqueVisitorId();
  const request = {
    retailerId,
    items: mapShoppingCartItemsToRequest(items),
  };

  const res = await fetch(
    DOMAIN + `/retailerCheckoutV2/removeItemsToUserRetailerShoppingCart`,
    {
      method: "POST",
      headers,
      signal,
      body: JSON.stringify(request),
    }
  );
  if (res.ok) {
    return await res.json();
  } else {
    throw new Error("Error updating items to user retailer shopping cart");
  }
};

export const createShareableShoppingCart = async (
  {
    retailerId,
    items,
  }: {
    retailerId: number;
    items: ShoppingCartItem[];
  },
  signal?: AbortSignal
): Promise<{
  shoppingCartId: string;
}> => {
  const headers = await getHeadersWithUniqueVisitorId();
  const request = {
    retailerId,
    items: mapShoppingCartItemsToRequest(items),
  };

  const res = await fetch(
    DOMAIN + `/retailerCheckoutV2/createShareableShoppingCart`,
    {
      method: "POST",
      headers,
      signal,
      body: JSON.stringify(request),
    }
  );
  if (res.ok) {
    return await res.json();
  } else {
    throw new Error("Error creating shareable shopping cart");
  }
};
